// A Computer player's decision-making for Halve It: unlike x01-ai.js (one
// continuous "aim -> throw -> re-aim" loop toward a single remaining score),
// each Halve It round is its own independent challenge (3 darts, except 3
// Colors - see its own simulateColorsChallenge - which can bust early) with
// its own target rule - so this is a dispatch table, one policy per round
// shape,
// mirroring the exact same dispatch controller.js's own simulateRoundScore()
// (the ?debug=1 data generator) already uses. Every policy still throws
// dart-by-dart through the same simulateThrow() x01-ai.js uses, so a real
// throwProfile's accuracyStdDev is what actually decides how often any of
// this lands, not the target choice itself.
//
// Every returned throw is annotated with `roundContribution` - how much of
// the round's *final* total that one dart actually accounts for (which can
// be 0 even when the dart itself scored something real, e.g. hitting the
// wrong number's wedge) - so the CPU overlay's live running total (see
// controller.js's animateCpuChallenge) always tracks the same number the
// round ends up recording, dart by dart, rather than a raw score that
// wouldn't match a round with partial or all-or-nothing scoring.
import halveIt from './definition.js';
import { simulateThrow } from '../../core/ai-throw-simulator.js';
import { WEDGE_ORDER, wedgeColor } from '../../core/dartboard-geometry.js';

function totalContribution(throws) {
    return throws.reduce((sum, t) => sum + t.roundContribution, 0);
}

// Hit-14-through-20 rounds: aim triple every dart, the same "let the
// accuracy stddev do the differentiating" policy x01-ai.js's own fallback
// target uses, rather than trying to model a smarter risk/reward choice.
// Any dart landing anywhere in the target's wedge (single/double/triple -
// the human keypad allows tapping all three; see components/keypad.js's
// dartTap/dartMaxMult) counts at its own real multiplier; landing in a
// different wedge, or off the board, is a plain miss for that one dart -
// exactly like a human simply not tapping for it.
function simulateNumberChallenge(target, throwProfile) {
    const throws = [1, 2, 3].map(() => simulateThrow({ number: target, ring: 'triple' }, throwProfile));
    throws.forEach(t => { t.roundContribution = t.result.number === target ? t.result.score : 0; });
    return { throws, totalScore: totalContribution(throws) };
}

// Bulls: dead center every dart - outer bull (25) or bullseye (50) both
// count, same as the human keypad's own bull widget (maxMultiplier: 2).
function simulateBullsChallenge(throwProfile) {
    const throws = [1, 2, 3].map(() => simulateThrow({ number: 25, ring: 'bullseye' }, throwProfile));
    throws.forEach(t => { t.roundContribution = (t.result.ring === 'bullseye' || t.result.ring === 'outerBull') ? t.result.score : 0; });
    return { throws, totalScore: totalContribution(throws) };
}

// Doubles/Triples: aim the same fixed ring at 20 every dart - "any double"/
// "any triple" is the real round's own rule, so a dart that lands in that
// ring at a *different* number still counts, at that number's own value.
function simulateRingChallenge(ring, throwProfile) {
    const throws = [1, 2, 3].map(() => simulateThrow({ number: 20, ring }, throwProfile));
    throws.forEach(t => { t.roundContribution = t.result.ring === ring ? t.result.score : 0; });
    return { throws, totalScore: totalContribution(throws) };
}

// 3 Adjacent means physically adjacent on the real board, and specifically
// 3 in a genuine STRAIGHT LINE - not just "each one touches something" (an
// earlier version of this file got that wrong: e.g. having already hit S7
// and D7, only T7 continues that line - a neighboring wedge's outer single
// touches S7 too, but going sideways there isn't the same line S7-D7 is
// already on). Two kinds of line exist on a real board:
//   - concentric: one wedge's own ring sequence, center to edge - bullseye,
//     outer bull, inner single, triple, (outer) single, double. Bullseye
//     only touches outer bull; outer bull also touches every inner single
//     (see the bull exception below); every other step only touches its own
//     immediate neighbors in this list, for the SAME wedge number.
//   - angular: one ring type going around the board - e.g. T20, T1 and T5
//     all touch in a chain, since 1 and 5 are 20's own real neighbors in
//     WEDGE_ORDER (not its neighbors by number). Given 2 already-hit wedges
//     on the same ring that are themselves neighbors (e.g. D7 and D19,
//     which are real neighbors), the only valid 3rd points are whichever
//     wedge extends the chain on either open end (D3 before 19, or D16
//     after 7 - see angularLineTargets).
// The one deliberate exception: outer bull touches EVERY inner single at
// once (it's a full ring, not tied to one wedge), so once it's paired with
// some inner single N, a *different* inner single is still a valid 3rd
// point even though it doesn't sit on N's own line - a star through the
// bull, not a straight line (see nextTargetGivenPair's own hub branch).
// Scored at each dart's real value, bull included.
function isAngularNeighbor(a, b) {
    const i = WEDGE_ORDER.indexOf(a);
    const j = WEDGE_ORDER.indexOf(b);
    if (i === -1 || j === -1) return false;
    const diff = Math.abs(i - j);
    return diff === 1 || diff === WEDGE_ORDER.length - 1; // adjacent, or wrapped around the ends
}

export function sameRegion(a, b) {
    return a.number === b.number && a.ring === b.ring;
}

// One-directional half of the touching relation - always called both ways
// (see touches()) so it doesn't matter which side a caller happens to check
// from. Used only where "does this touch that at all" is the real question
// (picking dart 2, with nothing yet established to stay in line with, and
// the inspector's own status messages) - dart 3's own target is decided by
// nextTargetGivenPair below, which is stricter than plain touching.
function touchesFrom(a, b) {
    if (a.ring === 'bullseye') return b.ring === 'outerBull';
    if (a.ring === 'outerBull') return b.ring === 'bullseye' || b.ring === 'innerSingle';
    if (a.ring === 'innerSingle') {
        return b.ring === 'outerBull' ||
            (b.ring === 'triple' && b.number === a.number) ||
            (b.ring === 'innerSingle' && isAngularNeighbor(a.number, b.number));
    }
    if (a.ring === 'triple') {
        return (b.ring === 'innerSingle' && b.number === a.number) ||
            (b.ring === 'single' && b.number === a.number) ||
            (b.ring === 'triple' && isAngularNeighbor(a.number, b.number));
    }
    if (a.ring === 'single') {
        return (b.ring === 'triple' && b.number === a.number) ||
            (b.ring === 'double' && b.number === a.number) ||
            (b.ring === 'single' && isAngularNeighbor(a.number, b.number));
    }
    if (a.ring === 'double') {
        return (b.ring === 'single' && b.number === a.number) ||
            (b.ring === 'double' && isAngularNeighbor(a.number, b.number));
    }
    return false;
}

export function touches(a, b) {
    return touchesFrom(a, b) || touchesFrom(b, a);
}

function regionValue(region) {
    if (region.ring === 'bullseye') return 50;
    if (region.ring === 'outerBull') return 25;
    const mult = { triple: 3, double: 2 }[region.ring] || 1; // single/innerSingle
    return region.number * mult;
}

// Three tiers, most preferred first: a plain single (outer or inner) beats
// single bull even when the bull would score more - a real dart, not the
// bull, is always the safer/more natural target when both are genuinely
// valid; single bull in turn beats a treble/double/bullseye, which are
// never worth the extra risk unless nothing safer is left at all. Highest
// real value wins within whichever tier actually has a candidate.
function pickBest(candidates) {
    if (!candidates.length) return null;
    const plainSingles = candidates.filter(r => r.ring === 'single' || r.ring === 'innerSingle');
    const bulls = candidates.filter(r => r.ring === 'outerBull');
    const pool = plainSingles.length ? plainSingles : bulls.length ? bulls : candidates;
    return pool.reduce((best, r) => (regionValue(r) > regionValue(best) ? r : best));
}

// Every region a dart could possibly land in - the bull twice (its two
// rings) plus each of the 20 numbers' 4 rings. Only used for dart 2's own
// general fallback (nextAdjacentTargetBySearch) - with nothing yet
// established beyond a single point, any touching neighbor legitimately
// starts a line (or the bull's own hub), so the broad touches() search is
// still the right tool there.
function allRegions() {
    const regions = [{ number: 25, ring: 'outerBull' }, { number: 25, ring: 'bullseye' }];
    for (let n = 1; n <= 20; n++) {
        regions.push({ number: n, ring: 'innerSingle' }, { number: n, ring: 'single' }, { number: n, ring: 'double' }, { number: n, ring: 'triple' });
    }
    return regions;
}

// The general fallback for dart 2 once dart 1 landed somewhere the fixed
// opening script (see scriptedSecondTarget) doesn't cover: every region
// that touches the one dart thrown so far and isn't a repeat of it,
// preferring anything that isn't a treble/double/bullseye, then the highest
// real value among whatever's left. Returns null only if dart 1 landed
// somewhere with no neighbors at all (never actually happens on a real
// board) - simulateAdjacentChallenge treats that as a foregone bust anyway.
function candidatesForSingleHit(hitSoFar) {
    return allRegions().filter(region =>
        !hitSoFar.some(h => sameRegion(h, region)) &&
        hitSoFar.some(h => touches(h, region))
    );
}

// Dart 1 always goes for single bull (outer bull) - it touches every inner
// single, the safest possible anchor for whatever comes next. Dart 2's
// target then depends on what dart 1 actually did: bull hit -> inner 20;
// an inner single instead -> single bull again (bull connects to it either
// way); a triple -> that same number's outer single (concentric neighbor),
// planning to close with its inner single; a double -> that same number's
// outer single too, planning to close with its treble instead. Anything
// else (the actual bullseye, an outer single, or a genuine surprise) falls
// through to the general search above.
function scriptedSecondTarget(dart1) {
    if (dart1.ring === 'outerBull') return { number: 20, ring: 'innerSingle' };
    if (dart1.ring === 'innerSingle') return { number: 25, ring: 'outerBull' };
    if (dart1.ring === 'triple' || dart1.ring === 'double') return { number: dart1.number, ring: 'single' };
    return null;
}

// One wedge's own concentric ring sequence, center to edge - shared by
// every wedge (bullseye/outer bull aren't number-specific, so they're
// filled in with 25 regardless of which wedge's line is being walked).
const CONCENTRIC_RINGS = ['bullseye', 'outerBull', 'innerSingle', 'triple', 'single', 'double'];

function ringAt(index, number) {
    const ring = CONCENTRIC_RINGS[index];
    return { number: (ring === 'bullseye' || ring === 'outerBull') ? 25 : number, ring };
}

// Which wedge's concentric line a pair sits on - whichever side actually
// carries a real wedge number; if neither does (both are bull rings), no
// wedge is pinned down yet, so default to 20, the same "aim the highest
// number" rule the rest of this file follows.
function concentricAxisNumber(r1, r2) {
    if (r1.ring !== 'bullseye' && r1.ring !== 'outerBull') return r1.number;
    if (r2.ring !== 'bullseye' && r2.ring !== 'outerBull') return r2.number;
    return 20;
}

// 2 wedges on the SAME ring - either real neighbors (extend the chain at
// the open end(s), e.g. D7+D19 -> D3 before 19, D16 after 7) or exactly one
// wedge apart with a single gap between them (fill that one gap, e.g.
// S16+S19 have only 7 between them on the board - see WEDGE_ORDER's own
// ...,3,19,7,16,8,... run - so S7 is the only way to make all 3
// consecutive). Anything further apart than that can never become 3
// consecutive by adding one more dart, so returns no candidates at all.
function angularLineTargets(numA, numB, ring) {
    const n = WEDGE_ORDER.length;
    let i = WEDGE_ORDER.indexOf(numA);
    let j = WEDGE_ORDER.indexOf(numB);
    let forwardGap = (j - i + n) % n;
    if (forwardGap > n / 2) { [i, j] = [j, i]; forwardGap = n - forwardGap; } // walk the shorter way from i to j
    if (forwardGap === 1) {
        return [
            { number: WEDGE_ORDER[(i - 1 + n) % n], ring },
            { number: WEDGE_ORDER[(j + 1) % n], ring }
        ];
    }
    if (forwardGap === 2) {
        return [{ number: WEDGE_ORDER[(i + 1) % n], ring }];
    }
    return [];
}

// Dart 3's target, given the 2 real darts actually thrown so far. Every
// rule that could apply contributes its own candidates - there's no single
// "the" rule here, since e.g. 2 different inner singles are always
// completable via the bull hub regardless of their own positions, but might
// *also* have a real angular gap-fill between them, both at once - so all
// applicable candidates are gathered first, then pickBest chooses among
// them. The rules themselves (see this file's own header):
//   - the bull hub exception - 2 different inner singles complete via the
//     bull, and the bull plus one inner single completes via *any* other
//     inner single, regardless of collinearity;
//   - one wedge's own concentric line (same number, or the shared
//     bullseye-outer-bull opening) - extend it if the pair's already
//     adjacent on it, or fill the one-wedge gap between them if they're
//     exactly 2 apart;
//   - one ring's own angular chain (real board neighbors) - same
//     extend-or-fill idea, going around the board instead of through one
//     wedge.
// Returns an empty list only if nothing above produced a single candidate -
// a foregone bust, since nothing can complete this specific pair at all.
function candidatesGivenPair(r1, r2) {
    const candidates = [];

    if (r1.ring === 'innerSingle' && r2.ring === 'innerSingle' && r1.number !== r2.number) {
        candidates.push({ number: 25, ring: 'outerBull' });
    }
    const bull = [r1, r2].find(r => r.ring === 'outerBull');
    const inner = [r1, r2].find(r => r.ring === 'innerSingle');
    if (bull && inner) {
        for (let n = 1; n <= 20; n++) {
            if (n !== inner.number) candidates.push({ number: n, ring: 'innerSingle' });
        }
    }

    // Same wedge's own axis - true whenever the pair doesn't name two
    // actually-different wedges. A bull ring (bullseye/outer bull) doesn't
    // carry a real wedge number of its own, so it never conflicts with
    // whatever number the OTHER side has - e.g. outer bull + triple 20 is
    // still "wedge 20's axis" even though outer bull isn't specific to 20,
    // exactly like outer bull + bullseye is (concentricAxisNumber below
    // works out which real number, if any, actually applies).
    const hasOwnNumber = r => r.ring !== 'bullseye' && r.ring !== 'outerBull';
    const conflictingWedges = hasOwnNumber(r1) && hasOwnNumber(r2) && r1.number !== r2.number;
    if (!conflictingWedges) {
        const idx1 = CONCENTRIC_RINGS.indexOf(r1.ring);
        const idx2 = CONCENTRIC_RINGS.indexOf(r2.ring);
        const lo = Math.min(idx1, idx2);
        const hi = Math.max(idx1, idx2);
        const number = concentricAxisNumber(r1, r2);
        if (hi === lo + 1) {
            if (lo - 1 >= 0) candidates.push(ringAt(lo - 1, number));
            if (hi + 1 < CONCENTRIC_RINGS.length) candidates.push(ringAt(hi + 1, number));
        } else if (hi === lo + 2) {
            candidates.push(ringAt(lo + 1, number));
        }
    }

    if (r1.ring === r2.ring && r1.ring !== 'bullseye' && r1.ring !== 'outerBull') {
        candidates.push(...angularLineTargets(r1.number, r2.number, r1.ring));
    }

    return candidates;
}

// Every legal target for the next dart, given the darts actually thrown so
// far - exported (alongside nextAdjacentTarget itself) purely so
// scripts/inspect-cpu-aim-halveit.mjs can show the full field of options a
// decision was chosen from, not just the winner. Dart 1 and a scripted
// dart 2 only ever have the one fixed answer; a fallback dart 2 or dart 3
// can have several.
export function adjacentCandidates(priorThrows) {
    if (priorThrows.length === 0) return [{ number: 25, ring: 'outerBull' }];
    const hits = priorThrows.map(t => ({ number: t.result.number, ring: t.result.ring }));
    if (priorThrows.length === 1) {
        const scripted = scriptedSecondTarget(hits[0]);
        return scripted ? [scripted] : candidatesForSingleHit(hits);
    }
    return candidatesGivenPair(hits[0], hits[1]);
}

export function nextAdjacentTarget(priorThrows) {
    return pickBest(adjacentCandidates(priorThrows));
}

// True circular consecutiveness (wrapping past index 19 back to 0) - e.g.
// positions {18, 19, 0} count, not just a plain sorted run.
function isConsecutiveCircular(positions, n) {
    const set = new Set(positions);
    if (set.size !== 3) return false;
    return positions.some(start => [start, (start + 1) % n, (start + 2) % n].every(p => set.has(p)));
}

// Whether 3 actual landings really do form one of the valid straight-line
// (or bull-hub) shapes described in this file's own header - re-derived
// from scratch here, the same way scoreColorsThrows/scoreFortyOneThrows
// validate their own rounds, rather than trusting how they happened to get
// thrown.
function isValidAdjacentTrio(regions) {
    for (let i = 0; i < regions.length; i++) {
        for (let j = i + 1; j < regions.length; j++) {
            if (sameRegion(regions[i], regions[j])) return false; // no repeats
        }
    }

    const bulls = regions.filter(r => r.ring === 'outerBull');
    const inners = regions.filter(r => r.ring === 'innerSingle');
    if (bulls.length === 1 && inners.length === 2) return true; // the bull-hub star

    const numbered = regions.filter(r => r.ring !== 'bullseye' && r.ring !== 'outerBull');
    if (numbered.length && numbered.every(r => r.number === numbered[0].number)) {
        const indices = regions.map(r => CONCENTRIC_RINGS.indexOf(r.ring)).sort((a, b) => a - b);
        if (indices[1] === indices[0] + 1 && indices[2] === indices[0] + 2) return true; // one wedge's own line
    }

    if (regions.every(r => r.ring === regions[0].ring) && !['bullseye', 'outerBull'].includes(regions[0].ring)) {
        const positions = regions.map(r => WEDGE_ORDER.indexOf(r.number));
        if (isConsecutiveCircular(positions, WEDGE_ORDER.length)) return true; // one ring's own chain
    }

    return false;
}

// Valid only if all 3 darts actually landed on the board and form one of
// the shapes isValidAdjacentTrio recognizes - scored at real value, bull
// included, same reasoning as 3 Colors' own scoreColorsThrows.
export function scoreAdjacentThrows(throws) {
    const regions = throws.map(t => ({ number: t.result.number, ring: t.result.ring }));
    const noMiss = throws.every(t => t.result.ring !== 'miss');
    const valid = throws.length === 3 && noMiss && isValidAdjacentTrio(regions);
    const total = valid ? throws.reduce((sum, t) => sum + t.result.score, 0) : 0;
    throws.forEach((t, i) => { t.roundContribution = i === throws.length - 1 ? total : 0; });
    return total;
}

// A miss busts the round on the spot, same as 3 Colors and Score 41. A
// repeat, or a dart 2 that doesn't actually touch dart 1 at all, dooms the
// round too (scoreAdjacentThrows will find no valid shape either way), but
// there's no need to check for those explicitly here: nextAdjacentTarget
// itself returns null the moment there's no legal way left to extend the
// line (including right after an invalid dart 2 - nextTargetGivenPair
// requires dart 1 and dart 2 to already fit a recognized shape), which
// already stops the loop before a foregone dart 3 gets thrown.
function simulateAdjacentChallenge(throwProfile) {
    const throws = [];
    for (let i = 0; i < 3; i++) {
        const target = nextAdjacentTarget(throws);
        if (!target) break;
        const thrown = simulateThrow(target, throwProfile);
        throws.push(thrown);
        if (thrown.result.ring === 'miss') break;
        const region = { number: thrown.result.number, ring: thrown.result.ring };
        const isRepeat = throws.slice(0, -1).some(t => sameRegion({ number: t.result.number, ring: t.result.ring }, region));
        if (isRepeat) break;
    }
    const totalScore = scoreAdjacentThrows(throws);
    return { throws, totalScore, outcome: totalScore > 0 ? 'hit' : 'bust' };
}

// 3 Colors: red and green are "hard" (they only exist on the bull and on
// the treble/double rings - genuinely small targets), black and white are
// "easy" (they cover the whole of every single bed - by far the largest
// area on the board). The strategy is 1 hard + 2 easy, not 2 hard + 1 easy:
// go for the bull first (dead center, the same aim the Bulls round itself
// uses) - the bullseye is red and the outer bull is green (see
// dartboard-geometry.js's own wedgeColor, read straight off dartboard.svg's
// real fill colors) - for as long as no hard color is in hand yet (landing
// on an ordinary wedge instead still counts as an easy color, but doesn't
// satisfy this). Once the 1 hard color needed is in hand, go for the
// highest-value easy color (S20, black) if neither easy is in hand yet, or
// specifically the *other* easy color once one is (e.g. already have red
// and black -> white next, never a second attempt at red/green). The one
// case with no clean line to follow - somehow already having *both* red and
// green (an unplanned outcome; darts 1 and 2 disagreeing about which hard
// color they landed on) - just takes any easy color, defaulting to black.
// Recomputed fresh from whatever colors the darts already thrown actually
// landed as - the same "decide again after every dart" idea as
// nextFortyOneTarget below, rather than a fixed 3-target plan.
export function nextColorTarget(priorThrows) {
    const colorsSoFar = new Set(priorThrows.map(t => wedgeColor(t.result.number, t.result.ring)).filter(Boolean));
    const hardCount = ['red', 'green'].filter(c => colorsSoFar.has(c)).length;
    const easyCount = ['black', 'white'].filter(c => colorsSoFar.has(c)).length;

    if (hardCount === 0) return { number: 25, ring: 'bullseye' }; // still need the round's 1 hard color
    if (easyCount === 0) return { number: 20, ring: 'single' }; // hard color secured, no easy yet - go for black, the higher-value easy
    if (easyCount === 1) return colorsSoFar.has('black') ? { number: 19, ring: 'single' } : { number: 20, ring: 'single' }; // the other easy color completes it
    return { number: 20, ring: 'single' }; // both easy colors already in hand somehow - not actually reachable with only 3 darts, kept as a safe default
}

// The challenge is 3 DISTINCT colors, so a dart that misses the board
// entirely, or lands in a color already in hand, busts the round on the
// spot - no more darts are thrown once that happens, the same "stop the
// instant it's decided" idea as x01-ai.js's own isBust check, rather than
// always throwing all 3 and only checking distinctness at the end. A
// successful run therefore always ends with exactly 3 darts and exactly 3
// distinct colors; every busted run ends after 1 or 2. Scored at each
// dart's real value (bull/treble included), not the raw-face-value-only
// rule 3 Adjacent uses - the whole point of chasing the bull/trebles here is
// to score big, so a real hit should count for what it's actually worth.
// All-or-nothing like Score 41 below, so only the dart that actually decided
// the round (its last one, bust or success) carries the real total - every
// earlier dart contributes 0 on its own.
export function scoreColorsThrows(throws) {
    const distinctColors = new Set(throws.map(t => wedgeColor(t.result.number, t.result.ring)).filter(Boolean));
    const total = (throws.length === 3 && distinctColors.size === 3) ? throws.reduce((sum, t) => sum + t.result.score, 0) : 0;
    throws.forEach((t, i) => { t.roundContribution = i === throws.length - 1 ? total : 0; });
    return total;
}

function simulateColorsChallenge(throwProfile) {
    const throws = [];
    for (let i = 0; i < 3; i++) {
        const target = nextColorTarget(throws);
        const thrown = simulateThrow(target, throwProfile);
        throws.push(thrown);
        const color = wedgeColor(thrown.result.number, thrown.result.ring);
        const colorsBefore = new Set(throws.slice(0, -1).map(t => wedgeColor(t.result.number, t.result.ring)).filter(Boolean));
        if (!color || colorsBefore.has(color)) break; // a miss, or a repeat color - busted
    }
    const totalScore = scoreColorsThrows(throws);
    return { throws, totalScore, outcome: totalScore > 0 ? 'hit' : 'bust' };
}

// Score 41 is purely binary (see components/keypad.js's fortyOneHit/
// fortyOneMiss) - but real halve-it "41" rules are stricter than just
// "the 3-dart total came to 41": it has to be 3 DIFFERENT numbers, never a
// double/treble except however the finishing dart needs to land, a miss
// anywhere busts it outright, and hitting a number already used - even via
// a different ring - busts it too (see simulateFortyOneChallenge's own bust
// checks, which apply all of this after every dart, not just at the end).
// Darts 1 and 2 follow a fixed opening route rather than a formula: start
// at single 10; if that's what actually landed, go for single 11 next; if
// dart 1 actually landed on 15 instead, go for single 9 next (10+11+20=41
// and 15+9+17=41 - both real, exact, 3-different-number routes, the same
// idea as x01's own checkoutFor chart, just for this one fixed total rather
// than every possible remaining). Anything else dart 1 actually lands on
// falls back to splitting whatever's left evenly across the two darts still
// to throw - still only ever a single, nudged off dart 1's own number so it
// doesn't immediately doom the round to a repeat.
export function nextFortyOneTarget(priorThrows) {
    if (priorThrows.length === 0) return { number: 10, ring: 'single' }; // start of the route
    const first = priorThrows[0].result.number;
    if (first === 10) return { number: 11, ring: 'single' };
    if (first === 15) return { number: 9, ring: 'single' };
    const remaining = 41 - priorThrows[0].result.score;
    let share = Math.max(1, Math.min(20, Math.round(remaining / 2)));
    while (share === first && share < 20) share += 1;
    while (share === first && share > 1) share -= 1;
    return { number: share, ring: 'single' };
}

// The finishing (3rd) dart has to reach `remaining` exactly, on a number
// neither of the first two darts already used - single if that fits under
// 20, else double, else treble, whichever lands on a real 1-20 wedge that
// isn't already spent, else the bull (outer bull is 25, bullseye is 50 -
// both count as "number" 25 for the never-repeat rule, same as
// dartboard-geometry.js's own resolvePoint models them). Returns null when
// no such dart exists at all - a genuine dead end (either `remaining` can't
// be split any legal way, e.g. 23, or every way that does exist would have
// to repeat an already-used number, including `remaining <= 0` - already
// blown past 41, or exactly on it with a dart still owed) -
// simulateFortyOneChallenge treats that as a foregone bust and never throws
// a third dart for it.
export function finishingFortyOneTarget(remaining, usedNumbers) {
    if (remaining >= 1 && remaining <= 20 && !usedNumbers.has(remaining)) {
        return { number: remaining, ring: 'single' };
    }
    if (remaining % 2 === 0 && remaining / 2 >= 1 && remaining / 2 <= 20 && !usedNumbers.has(remaining / 2)) {
        return { number: remaining / 2, ring: 'double' };
    }
    if (remaining % 3 === 0 && remaining / 3 >= 1 && remaining / 3 <= 20 && !usedNumbers.has(remaining / 3)) {
        return { number: remaining / 3, ring: 'triple' };
    }
    if (!usedNumbers.has(25)) {
        if (remaining === 25) return { number: 25, ring: 'outerBull' };
        if (remaining === 50) return { number: 25, ring: 'bullseye' };
    }
    return null;
}

// All-or-nothing like 3 Colors above - only the dart that actually decided
// the round (its last one, bust or success) carries the real total.
export function scoreFortyOneThrows(throws) {
    const runningTotal = throws.reduce((sum, t) => sum + t.result.score, 0);
    const total = (throws.length === 3 && runningTotal === 41) ? 41 : 0;
    throws.forEach((t, i) => { t.roundContribution = i === throws.length - 1 ? total : 0; });
    return total;
}

// A miss or a repeated number busts the round on the spot, exactly like 3
// Colors' own early stop (see its simulateColorsChallenge) - checked after
// every dart, not just the planned ones, since an off-route dart 1/2 can
// still accidentally repeat a number a moment later. Before the 3rd dart,
// finishingFortyOneTarget's own null return (no legal, non-repeating finish
// exists) busts it just as outright, without a third dart ever being
// thrown for a doomed attempt.
function simulateFortyOneChallenge(throwProfile) {
    const throws = [];
    let busted = false;
    for (let i = 0; i < 3 && !busted; i++) {
        let target;
        if (i === 2) {
            const remaining = 41 - throws.reduce((sum, t) => sum + t.result.score, 0);
            const usedNumbers = new Set(throws.map(t => t.result.number).filter(n => n != null));
            target = finishingFortyOneTarget(remaining, usedNumbers);
            if (!target) { busted = true; break; }
        } else {
            target = nextFortyOneTarget(throws);
        }
        const thrown = simulateThrow(target, throwProfile);
        throws.push(thrown);
        const isRepeat = throws.slice(0, -1).some(t => t.result.number === thrown.result.number);
        if (thrown.result.ring === 'miss' || isRepeat) busted = true;
    }
    const totalScore = scoreFortyOneThrows(throws);
    const outcome = busted ? 'bust' : (totalScore === 41 ? 'hit' : 'halved');
    return { throws, totalScore, outcome };
}

// Plans a Computer player's entire challenge for one round: every dart
// actually thrown (3, unless 3 Colors busts early - see simulateColorsChallenge),
// with its aim/actual landing/result/roundContribution (so the CPU overlay
// can reveal them the same way x01's does), plus the round's final score.
// `outcome` is 'hit' for a real score, 'bust' for 3 Colors ending early on a
// miss/repeat color, or 'halved' for any other round reaching 0 the ordinary
// way (all 3 darts thrown, none of them counting) - controller.js's own
// halveItCellContent treats 'bust' and 'halved' identically (both are just a
// 0 that halves the running total); the distinction only matters for the
// CPU overlay's own outcome label (see cpuChallengeOutcomeLabel).
export function simulateAiChallenge({ round, throwProfile }) {
    const widget = halveIt.inputWidgetFor(round);
    const result =
        widget.type === 'dartboard-number' ? simulateNumberChallenge(widget.target, throwProfile) :
        widget.type === 'dartboard-bull' ? simulateBullsChallenge(throwProfile) :
        widget.type === 'fortyone' ? simulateFortyOneChallenge(throwProfile) :
        round.name === 'Doubles' ? simulateRingChallenge('double', throwProfile) :
        round.name === 'Triples' ? simulateRingChallenge('triple', throwProfile) :
        round.name === '3 Adjacent' ? simulateAdjacentChallenge(throwProfile) :
        simulateColorsChallenge(throwProfile); // '3 Colors'

    const outcome = result.outcome || (result.totalScore === 0 ? 'halved' : 'hit');
    return { throws: result.throws, totalScore: result.totalScore, outcome };
}
